import { ThemeComponent,  } from "@/theme/ThemeComponent";
import { Component, Prop, Ref, SimpleEvent } from "@eastsideco/salvo-ts";
import { elevate, FacetParams, type SearchPage, type SortType, type SearchOrigin } from '@apptus/esales-api';
import { VoyadoProductCard } from "./VoyadoProductCard";
import { VoyadoFilters } from "./VoyadoFilters";
import { getLocalisedElevate } from "@/utils/elevate";

declare global {
    interface Window {
        elevate: ReturnType<typeof elevate>;
    }
}

@Component
export class VoyadoSearchResults extends ThemeComponent {
    @Ref grid!: HTMLDivElement;
    @Ref loadPrevious!: HTMLButtonElement;
    @Ref loadNext!: HTMLButtonElement;
    @Ref productCount!: HTMLSpanElement;
    @Ref filterBar!: VoyadoFilters;
    @Ref resultsContainer!: HTMLElement;
    @Ref noResultsContainer!: HTMLElement;
    @Ref searchHeader!: HTMLElement;
    @Ref searchHeaderProductCount!: HTMLElement;
    @Ref searchHeaderSearchTerm!: HTMLElement;

    @Ref similarSearchesContainer!: HTMLElement;
    @Ref similarSearchesResults!: HTMLElement;

    @Ref noResultsSimilarSearchesContainer!: HTMLElement;
    @Ref noResultsSimilarSearchesResults!: HTMLElement;

    @Ref({ required: false }) autoCorrectNotice!: HTMLElement;

    @Ref noResultsSearchForm!: HTMLElement;
    @Ref noResultsSearchInput!: HTMLInputElement;
    @Ref noResultsSearchButton!: HTMLButtonElement;

    @Prop({ default: 'dress,skirt,top' }) suggested?: string;
    @Prop({ default: 16 }) limit!: number;

    private _page: number = 1;
    private result?: SearchPage;

    private currentQuery?: string;
    private loadedPages: number[] = [];
    private loadedProductCount = 0;
    private totalProductCount = 0;
    private facets?: FacetParams;
    private sort?: SortType;

    private _onVoyadoSearchResultsLoaded = new SimpleEvent<SearchPage>();

    public get onVoyadoSearchResultsLoaded() {
        return this._onVoyadoSearchResultsLoaded.asEvent();
    }

    private voyadoSearchResultsLoaded(searchPage: SearchPage) {
        this._onVoyadoSearchResultsLoaded.dispatch(searchPage);
    }

    private static readonly validOrigins: SearchOrigin[] = [
        'ORGANIC', 'PHRASE_SUGGEST', 'DID_YOU_MEAN',
        'UNDO_AUTO_CORRECT', 'RELATED_SEARCHES', 'RECENT_SEARCH',
    ];

    get terms() {
        const params = new URLSearchParams(document.location.search);
        return params.get('q') || '';
    }

    /**
     * How the shopper arrived at this search, read from the URL so the
     * search form can signal it across the page navigation. Defaults to
     * 'ORGANIC' (typed search / direct hit). Sent on every searchPage call —
     * Elevate marks this optional only for backwards compatibility and will
     * require it in the next major storefront API version.
     */
    get origin(): SearchOrigin {
        const param = new URLSearchParams(document.location.search).get('origin');
        return VoyadoSearchResults.validOrigins.includes(param as SearchOrigin)
            ? (param as SearchOrigin)
            : 'ORGANIC';
    }

    set page(value: number) {
        this._page = value;

        const url = new URL(window.location.href);
        url.searchParams.set('page', this.page.toString());
        history.replaceState({}, "", url);

        this.loadedPages.push(value);

        this.setButtonVisibility();
        this.setHeaderContent();
        void this.showSimilarSearches();
    }

    get page() {
        return this._page;
    }

    get skip() {
        if (! this.page) {
            this.page = 1;
        }

        return this.limit * (this.page - 1);
    }

    get previousPage() {
        const earliestPage = Math.min(...this.loadedPages);
        return earliestPage > 1 ? earliestPage - 1 : null;
    }

    get nextPage() {
        const lastPage = Math.max(...this.loadedPages);
        return lastPage + 1;
    }

    get progressLabel() {
        return `Showing ${this.loadedProductCount} of ${this.totalProductCount}`;
    }

    get progressPercent() {
        return (100 * this.loadedProductCount) / this.totalProductCount;
    }

    get hasLoadMore() {
        const [lastPage] = this.loadedPages.sort().reverse();
        return (this.limit * lastPage) < this.totalProductCount;
    }

    init() {
        this.setInitialPage();

        const interval = setInterval(() => {
            if (!window.elevate) {
                return;
            }

            void this.fetchSearchResults();
            this.addLoadMoreListeners();
            this.addFilterListeners();

            clearInterval(interval);
        }, 200);

        this.addSearchLinkListeners();
    }

    setInitialPage() {
        const params = new URLSearchParams(document.location.search);
        this.page = parseInt(params.get("page") || "1", 10);
    }

    addSearchListeners() {
        this.$listen(this.noResultsSearchInput, 'keydown', (event) => {
            if (event.code === 'Enter') {
                window.location.href = `/search?q=${encodeURI(this.noResultsSearchInput.value)}`;
            }
        });

        this.$listen(this.noResultsSearchButton, 'click', () => {
            window.location.href = `/search?q=${encodeURI(this.noResultsSearchInput.value)}`;
        });
    }

    async fetchSearchResults(insert: 'replace' | 'prepend' | 'append' = 'replace', resetCount = false) {
        if (!window.elevate) {
            return;
        }

        this.result = await getLocalisedElevate().query.searchPage({
            q: this.currentQuery ?? this.terms,
            origin: this.origin,
            limit: this.limit,
            skip: this.skip,
            sort: this.sort,
            facets: this.facets,
        });

        this.voyadoSearchResultsLoaded(this.result);

        this.currentQuery = this.result.autoCorrect?.q ?? this.result.q;

        this.setResultsVisibility(this.result.primaryList.totalHits);
        this.updateProductCounts(resetCount);
        this.showCollection(insert);
        this.setButtonVisibility();
        this.setHeaderContent();
        void this.showSimilarSearches();
        this.updateTitle();

        this.filterBar.setResult(this.result);
    }

    updateTitle() {
        if (! this.result) {
            return;
        }

        const totalHits = this.result?.primaryList.totalHits;
        const searchTerm = this.result?.autoCorrect?.q || this.result.q;
        const newTitle = `Search: ${totalHits} results found for "${searchTerm}"`;

        document.title = newTitle;
    }

    renderAutoCorrectNotice() {
        if (!this.autoCorrectNotice) {
            return;
        }

        const autoCorrect = this.result?.autoCorrect;

        if (!autoCorrect) {
            return;
        }

        // `autoCorrect.q` is the corrected term Elevate used; `result.q` echoes
        // what the shopper actually typed (used for the "search instead" undo).
        const corrected = document.createElement('strong');
        corrected.textContent = autoCorrect.q;

        const typed = this.result?.q ?? this.terms;

        const undoLink = document.createElement('a');
        undoLink.className = 'voyado-search-results__autocorrect-undo';
        undoLink.href = `/search?q=${encodeURIComponent(typed)}&origin=UNDO_AUTO_CORRECT`;
        undoLink.textContent = typed;

        this.autoCorrectNotice.replaceChildren(
            document.createTextNode('Showing results for '),
            corrected,
            document.createTextNode(' instead of '),
            undoLink,
            document.createTextNode('.'),
        );
        this.autoCorrectNotice.hidden = false;
    }

    setResultsVisibility(resultCount: number) {
        this.resultsContainer.classList.toggle('container--hidden', resultCount === 0);
        this.noResultsContainer.classList.toggle('container--hidden', resultCount !== 0);
        this.classList.toggle('voyado-search-results--no-results', resultCount === 0);

        this.noResultsSearchForm.setAttribute('action', '/search');
    }

    updateProductCounts(resetCount: boolean) {
        if (!this.result?.primaryList) {
            return;
        }

        this.loadedProductCount =
            (resetCount ? 0 : this.loadedProductCount) + this.result.primaryList.productGroups.length;
        this.totalProductCount = this.result.primaryList.totalHits;

        this.productCount.innerText = this.progressLabel;
        this.style.setProperty('--progress', `${this.progressPercent}%`);

    }

    showCollection(insert: 'replace' | 'prepend' | 'append' = 'replace') {
        if (!this.result?.primaryList?.productGroups) {
            return;
        }

        const documentFragment = document.createDocumentFragment();

        for (const productGroup of this.result.primaryList.productGroups) {
            const card = new VoyadoProductCard;
            card.productGroup = productGroup;

            documentFragment.appendChild(card);
        }

        switch (insert) {
            case 'replace':
                this.grid.replaceChildren(documentFragment);
                break;
            case 'prepend':
                this.grid.prepend(documentFragment);
                break;
            case 'append':
                this.grid.append(documentFragment);
                break;
        }
    }

    setButtonVisibility() {
        this.loadPrevious.classList.toggle('voyado-search-results__load-more--hidden', this.previousPage === null);
        this.loadNext.classList.toggle('voyado-search-results__load-more--hidden', !this.hasLoadMore);
    }

    setHeaderContent() {
        this.searchHeaderProductCount.innerText = `Showing ${this.totalProductCount} products`;

        const autoCorrect = this.result?.autoCorrect;
        const typed = this.result?.q ?? this.terms;

        if (autoCorrect) {
            this.searchHeaderSearchTerm.innerHTML = `
                Search results for "${autoCorrect.q}" instead of 
                "<a href="/search?q=${encodeURIComponent(typed)}&origin=UNDO_AUTO_CORRECT">${typed}</a>".
            `;
        } else {
            this.searchHeaderSearchTerm.innerText = `Search results for "${this.terms}"`;
        }

        this.searchHeader.classList.remove('voyado-search-results__header--hidden');

    }

    async showSimilarSearches() {
        const result = await getLocalisedElevate().query.autocomplete({ q: this.terms });

        const relatedSearchesHTML = result.phraseSuggestions?.map((term) => `
            <a href="/search?q=${encodeURI(term.q)}">
                ${(decodeURI(term.q))}
            </a>
        `).slice(1, 4).join('');

        const fallbackRelatedSearchesHTML = this.suggested?.split(',').map((term) => `
            <a href="/search?q=${encodeURI(term)}">
                ${(decodeURI(term))}
            </a>
        `).slice(0, 3).join('');

        // We're just displaying the default content at
        // if (relatedSearchesHTML) {
        //     this.similarSearchesResults.innerHTML = relatedSearchesHTML;
        //     this.noResultsSimilarSearchesResults.innerHTML = relatedSearchesHTML;
        // } else if (fallbackRelatedSearchesHTML) {
        //     this.similarSearchesResults.innerHTML = fallbackRelatedSearchesHTML;
        //     this.noResultsSimilarSearchesResults.innerHTML = fallbackRelatedSearchesHTML;
        // }

        if (fallbackRelatedSearchesHTML) {
            this.similarSearchesResults.innerHTML = fallbackRelatedSearchesHTML;
            this.noResultsSimilarSearchesResults.innerHTML = fallbackRelatedSearchesHTML;
        }

        this.similarSearchesContainer.classList.toggle(
            'voyado-search-results__similar--hidden',
            relatedSearchesHTML === '' && fallbackRelatedSearchesHTML === ''
        );

        this.noResultsSimilarSearchesContainer.classList.toggle(
            'voyado-search-results__similar--hidden',
            relatedSearchesHTML === '' && fallbackRelatedSearchesHTML === ''
        );
    }

    addLoadMoreListeners() {
        this.$listen(this.loadPrevious, 'click', () => {
            if (this.previousPage != null) {
                this.page = this.previousPage;
                void this.fetchSearchResults('prepend');
            }
        });

        this.$listen(this.loadNext, 'click', () => {
            this.page = this.nextPage;

            void this.fetchSearchResults('append');
        });
    }

    addFilterListeners() {
        this.$subscribe(this.filterBar.onChange, () => {
            const values = this.filterBar.values;

            this.sort = values.sortBy;
            this.facets = values.facetParams;

            this.loadedPages = [];
            this.page = 1;

            void this.fetchSearchResults('replace', true);
        });
    }

    addSearchLinkListeners() {
        this.$listen(this.similarSearchesResults, 'click', (event: MouseEvent) => {
            event.preventDefault();

            if (! (event.target as HTMLElement)?.matches('a')) {
                return;
            }

            const link = event.target as HTMLAnchorElement;

            window.location.href = `/search?q=${encodeURI(link.innerText)}`;
        });

        this.$listen(this.noResultsSimilarSearchesResults, 'click', (event: MouseEvent) => {
            event.preventDefault();

            if (! (event.target as HTMLElement)?.matches('a')) {
                return;
            }

            const link = event.target as HTMLAnchorElement;

            window.location.href = `/search?q=${encodeURI(link.innerText)}`;
        });
    }
}
